import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangleIcon, XCloseIcon, YesNoToggle } from './ui'

interface ClaimLegalToDriveModalProps {
  onClose: () => void
  onConfirm: () => void
  /** User indicates the vehicle is not safe / legal to drive — exit self-serve (e.g. navigate to call-for-assistance). */
  onVehicleNotSafe: () => void
}

const UNSAFE_TO_DRIVE_BULLETS: string[] = [
  "The lights or brakes aren't working",
  'Airbags have deployed',
  "Doors, boot or bonnet won't close",
  'There are leaking fluids or signs of engine damage',
  "There is a windscreen chip/crack that is obscuring the driver's view or compromising the integrity of the windscreen",
]

/** Matches common tabbable elements; excludes disabled and tabindex="-1". */
const TABBABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

function listTabbableIn(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)).filter((el) => {
    if (el.closest('[inert]')) return false
    if (el.getAttribute('aria-hidden') === 'true') return false
    return el.tabIndex >= 0
  })
}

/**
 * Pre-navigation check: user confirms whether the vehicle is safe and legal to drive before continuing a claim.
 */
export default function ClaimLegalToDriveModal({
  onClose,
  onConfirm,
  onVehicleNotSafe,
}: ClaimLegalToDriveModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const previousActiveRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    previousActiveRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null

    const root = document.getElementById('root')
    if (root) {
      root.setAttribute('aria-hidden', 'true')
      ;(root as HTMLElement).inert = true
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        onClose()
        return
      }

      if (e.key !== 'Tab') return

      const container = dialogRef.current
      if (!container) return

      const nodes = listTabbableIn(container)
      if (nodes.length === 0) return

      const first = nodes[0]
      const last = nodes[nodes.length - 1]
      const active = document.activeElement

      if (e.shiftKey) {
        if (active === first || !container.contains(active)) {
          e.preventDefault()
          last.focus()
        }
      } else if (active === last || !container.contains(active)) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown, true)

    const raf = requestAnimationFrame(() => {
      const container = dialogRef.current
      if (!container) return
      const nodes = listTabbableIn(container)
      if (nodes.length > 0) {
        nodes[0].focus()
      } else {
        container.tabIndex = -1
        container.focus()
      }
    })

    return () => {
      cancelAnimationFrame(raf)
      document.removeEventListener('keydown', handleKeyDown, true)
      if (root) {
        root.removeAttribute('aria-hidden')
        ;(root as HTMLElement).inert = false
      }
      previousActiveRef.current?.focus()
    }
  }, [onClose])

  const modal = (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center py-[16px] md:py-[64px] px-[16px] transition-opacity duration-300 opacity-100"
      role="dialog"
      aria-modal="true"
      aria-labelledby="claim-legal-to-drive-title"
    >
      <div className="absolute inset-0 backdrop-blur-sm" aria-hidden>
        <div className="absolute bg-background-overlay inset-0 opacity-50" />
      </div>

      <div className="bg-background-primary flex flex-col items-center overflow-hidden relative rounded-2xl shadow-xl shrink-0 w-full max-w-[480px] max-h-[calc(100vh-32px)] md:max-h-[calc(100vh-128px)]">
        <div className="flex flex-col items-start relative flex-1 overflow-y-auto w-full min-h-0 overscroll-contain">
          <div className="flex flex-col gap-[16px] items-start pt-[20px] px-[16px] pb-[24px] md:pb-[32px] relative shrink-0 w-full">
            <button
              onClick={onClose}
              className="absolute cursor-pointer flex items-center justify-center overflow-clip p-[8px] right-[12px] rounded-md size-[44px] top-[12px] z-10"
              aria-label="Close"
              type="button"
            >
              <div className="overflow-clip relative shrink-0 size-6 text-text-tertiary-600">
                <XCloseIcon className="w-6 h-6" />
              </div>
            </button>

            <div className="bg-background-warning-secondary overflow-clip relative rounded-full shrink-0 size-12 flex items-center justify-center">
              <div className="overflow-clip relative shrink-0 size-6 text-warning-primary">
                <AlertTriangleIcon className="w-6 h-6" aria-hidden />
              </div>
            </div>

            <div className="flex flex-col gap-[12px] items-start relative shrink-0 w-full pr-[40px]">
              <p
                id="claim-legal-to-drive-title"
                className="font-display font-semibold leading-[32px] relative shrink-0 text-text-primary-900 text-display-xs w-full"
              >
                Is your vehicle safe and legal to drive?
              </p>
              <div className="font-body font-regular leading-[24px] text-text-secondary-700 text-md w-full">
                <p className="mb-[10px]">
                  If your vehicle has any of the following it may not be safe to drive
                </p>
                <ul className="list-disc pl-[20px] space-y-[6px] m-0">
                  {UNSAFE_TO_DRIVE_BULLETS.map((line) => (
                    <li key={line} className="pl-[2px]">
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-start pb-[16px] md:pb-[24px] pt-[8px] px-[16px] relative shrink-0 w-full bg-background-primary border-t border-border-secondary md:border-0">
          <YesNoToggle
            onChange={(answer) => {
              if (answer === 'Yes') onConfirm()
              else onVehicleNotSafe()
            }}
          />
        </div>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}
